
"use client";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Search, User, Heart, ShoppingBag, ChevronDown, Menu, X } from "lucide-react";
import Link from "next/link";
import logo from "../../../../public/image/logo.webp";
import { useUser } from "@/providers/userContext";
import { usePathname } from "next/navigation";
import Image from "next/image";
import { getLogo } from "@/services/storeService";

// Debounce utility for scroll event
const debounce = (func: any, wait: any) => {
  let timeout: any;
  return (...args: any) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

type Category = {
  seo_url: string;
  id: number;
  name: string;
  parent_id: number;
  sortorder?: number;
};

const Navbar = ({ categoryListData }: { categoryListData: Category[] }) => {
  const { login, isLoggedIn, user, cartinfoData } = useUser();
  const pathname = usePathname();
  const inputRef = useRef<HTMLInputElement>(null);

  // State management
  const [state, setState] = useState({
    hideFirstSec: false,
    showSearchBar: false,
    isOpen: false,
    searchQuery: "",
    expandedMainCategory: null as string | null,
    expandedSubCategory: null as string | null,
    expandedSubSubCategory: null as string | null,
  });

  const {
    hideFirstSec,
    showSearchBar,
    isOpen,
    searchQuery,
    expandedMainCategory,
    expandedSubCategory,
    expandedSubSubCategory,
  } = state;

  // Memoize mainCategories
  const mainCategories = useMemo(() => {
    return categoryListData
      ?.sort((a: Category, b: Category) => (a.sortorder || 0) - (b.sortorder || 0))
      .filter((category: Category) => category.parent_id === 0)
      .sort((a: Category, b: Category) => a.id - b.id);
  }, [categoryListData]);

  // Memoize userData and cartCount
  const userData = useMemo(() => user ?? null, [user]);
  const cartCount = useMemo(() => cartinfoData?.cartcount ?? 0, [cartinfoData]);

  // Handle scroll with debouncing
  // const handleScroll = useCallback(
  //   debounce(() => {
  //     setState((prev) => ({ ...prev, hideFirstSec: window.scrollY > 50 }));
  //   }, 100),
  //   []
  // );

  // useEffect(() => {
  //   window.addEventListener("scroll", handleScroll);
  //   return () => window.removeEventListener("scroll", handleScroll);
  // }, [handleScroll]);

  // Autofocus search input when search bar opens
  useEffect(() => {
    if (showSearchBar && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showSearchBar]);

  // Lock body scroll when menu is open
  useEffect(() => {
    const original = document.body.style.overflow;
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = original || "";
    }
    return () => {
      document.body.style.overflow = original || "";
    };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setState((prev) => ({ ...prev, isOpen: false }));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Toggle functions
  const toggleSearchBar = () => {
    setState((prev) => ({ ...prev, showSearchBar: !prev.showSearchBar }));
  };

  const toggleMenu = () => {
    setState((prev) => ({ ...prev, isOpen: !prev.isOpen }));
  };

  const toggleCategory = (categoryId: string, key: keyof typeof state) => {
    setState((prev) => ({
      ...prev,
      [key]: prev[key] === categoryId ? null : categoryId,
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({ ...prev, searchQuery: e.target.value }));
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery) {
      // Convert to lowercase and replace spaces with hyphens
      const formattedQuery = searchQuery
        .toLowerCase()
        .replace(/\s+/g, '-');
      window.location.href = `/products/${encodeURIComponent(formattedQuery)}`;
    }
  };

  const getSubcategoriesByParentId = (parentId: number) => {
    return categoryListData?.filter((category: Category) => category.parent_id === parentId) || [];
  };

  const hasSubcategories = (parentId: number) => getSubcategoriesByParentId(parentId).length > 0;

  // Check if component should be hidden on mobile
  const [isMobile, setIsMobile] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);

    let resizeTimeout: any;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkMobile, 150);
    };

    checkMobile();
    window.addEventListener("resize", handleResize);
    setHasMounted(true);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Prevent SSR flickering
  const shouldHideNavbar =
    pathname.includes("/signin") ||
    pathname.includes("/cart") ||
    pathname.includes("/checkout") ||
    pathname.startsWith("/p/");
  if (hasMounted && isMobile && shouldHideNavbar) return null;

  return (
    <>

      <div className=" flex justify-center items-center !space-x-[20px] !bg-[#C7DF9C] !py-[10px]">
        <p className="!text-primary trxt-[14px] font-medium">Rakhi Sale is Live</p>
        <a className=" !text-primary trxt-[14px] font-medium underline" href="">Shop Now</a>
      </div>

      <header className="py-[10px] bg-[#FBFBF8]  sticky top-0 z-[70] !px-[16px]">
        {/* Welcome Section */}
        {/* {!hideFirstSec && (
        <div className="w-full py-2 px-3 mx-auto bg-cream">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35" />
                <path d="M6 18h12" />
                <path d="M6 14h12" />
              </svg>
              <a href="tel:+917668864477" className="text-black">+91 7668864477</a>
            </div>
            <div className="text-center py-2">
              <p className="text-2xl font-arbutus text-dark-green">Welcome to Brass Idols</p>
            </div>
            <div className="flex gap-2 text-black">
              <Link href="/aboutus" className="hover:opacity-70">About</Link>
              <span>|</span>
              <Link href="/contactus" className="hover:opacity-70">Contact</Link>
              <span>|</span>
              <Link href="/storelocation" className="hover:opacity-70">Store Location</Link>
            </div>
          </div>
        </div>
      )} */}

        <div className="max-w-[1600px] mx-auto flex items-center justify-between ">


          {/* Left Navigation */}
          <nav className="w-4/12 ">
            <ul className="hidden lg:flex items-center gap-[16px] font-arbutus text-[#484C3F] text-base">
              <li className="flex items-center text-[16px]">
                <Link href="/" className="hover:opacity-70 transition-opacity">Home</Link>
              </li>
              <li className="flex items-center text-[16px]">
                <Link href="/products/full-length" className="hover:opacity-70 transition-opacity whitespace-nowrap">New Arrivals</Link>
              </li>
              <li className="flex items-center text-[16px]">
                <Link href="/products/full-length" className="hover:opacity-70 transition-opacity whitespace-nowrap">Essential Wear</Link>
              </li>
              {/* <li className="flex items-center text-[16px]">
              <Link href="/" className="hover:opacity-70 transition-opacity">Collection</Link>
            </li>
            <li className="flex items-center text-[16px]">
              <Link href="/" className="hover:opacity-70 transition-opacity whitespace-nowrap">Exclusives</Link>
            </li>
            <li className="flex items-center text-[16px]">
              <Link href="/" className="hover:opacity-70 transition-opacity whitespace-nowrap">New Arrivals</Link>
            </li>
            <li className="flex items-center text-[16px]">
              <Link href="/" className="hover:opacity-70 transition-opacity whitespace-nowrap">Essential Wear</Link>
            </li> */}
              {mainCategories?.slice(0, 5).map((mainCategory) => (
                <li key={mainCategory.id} className="flex items-center relative group">
                  <Link
                    href={`/products/${mainCategory.seo_url}`}
                    className="hover:opacity-70 transition-opacity !whitespace-nowrap"
                  >
                    {mainCategory.name}
                  </Link>
                  {hasSubcategories(mainCategory.id) && (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      <ul className="absolute top-full left-0 bg-white shadow-md z-10 hidden group-hover:block min-w-[200px]">
                        {getSubcategoriesByParentId(mainCategory.id).map((subCategory) => (
                          <li key={subCategory.id} className="relative group">
                            <Link
                              href={`/products/${subCategory.seo_url}`}
                              className="block px-4 py-2 text-[#484C3F] hover:bg-dark-green/5 !whitespace-nowrap"
                            >
                              {subCategory.name}
                            </Link>
                            {hasSubcategories(subCategory.id) && (
                              <ul className="absolute left-full top-0 bg-white shadow-md z-10 hidden group-hover:block min-w-[200px]">
                                {getSubcategoriesByParentId(subCategory.id).map((subSubCategory) => (
                                  <li key={subSubCategory.id} className="relative group">
                                    <Link
                                      href={`/products/${subSubCategory.seo_url}`}
                                      className="block px-4 py-2 text-dark-green hover:bg-dark-green/5 !whitespace-nowrap"
                                    >
                                      {subSubCategory.name}
                                    </Link>
                                    {hasSubcategories(subSubCategory.id) && (
                                      <ul className="absolute left-full top-0 bg-white shadow-md z-10 hidden group-hover:block min-w-[200px]">
                                        {getSubcategoriesByParentId(subSubCategory.id).map(
                                          (subSubSubCategory) => (
                                            <li key={subSubSubCategory.id}>
                                              <Link
                                                href={`/products/${subSubSubCategory.seo_url}`}
                                                className="block px-4 py-2 text-dark-green hover:bg-dark-green/5 !whitespace-nowrap"
                                              >
                                                {subSubSubCategory.name}
                                              </Link>
                                            </li>
                                          )
                                        )}
                                      </ul>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </li>
              ))}

            </ul>

            {/* Mobile Menu Button */}
            <div className="flex items-center sm:gap-x-[40px] gap-x-[20px]">
              <button
                aria-label="Open menu"
                aria-expanded={isOpen}
                onClick={toggleMenu}
                className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-dark-green hover:bg-dark-green/5"
              >
                <Menu className="w-4 h-4" />
              </button>

              <Link href="/" className="md:hidden block">
                <img
                  className="w-32"
                  src={getLogo()}
                  alt="Store Logo"
                  width="auto"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "https://vilva-backup.blr1.digitaloceanspaces.com/vilvabusiness/common%20files/empty-logowebp.webp";
                  }}
                />
              </Link>
            </div>
          </nav>

          {/* Logo - Center */}
          <div className="w-4/12 flex justify-center   ">
            <Link href="/" className="hidden md:block">
              <img
                className="w-32"
                src={getLogo()}
                alt="Store Logo"
                width="auto"
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "https://vilva-backup.blr1.digitaloceanspaces.com/vilvabusiness/common%20files/empty-logowebp.webp";
                }}
              />
            </Link>
          </div>

          {/* Right Side - Search and Icons */}
          <div className="w-4/12 flex justify-end !pr-[10px]">
            <div className="flex items-center gap-[20px]">

              {/* Mobile Search Icon */}
              <div className="hidden ">
                <Search
                  className="w-4 h-4 text-dark-green cursor-pointer"
                  onClick={toggleSearchBar}
                />
              </div>

              {/* Icons */}
              <div className="flex items-center gap-[16px]">


                <div className="hidden lg:flex items-center justify-between px-[16px] py-[6px] border border-[#C7DF9C] rounded-[20px]">
                  <input
                    type="text"
                    placeholder="Search Products"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="flex-1 font-arbutus text-base text-[#484C3F] !placeholder-[#AEAEAE] bg-transparent outline-none"
                  />
                  <Search
                    className="w-4 h-4 text-[#484C3F] flex-shrink-0 cursor-pointer"
                    onClick={handleSearchSubmit}
                  />
                </div>

                <div className="lg:hidden">
                  <Search
                    className="w-4 h-4 text-[#484C3F] hover:opacity-70 transition-opacity cursor-pointer"
                    onClick={toggleSearchBar}
                  />
                </div>

                <Link href={userData ? "/myaccount" : "/signin"}>
                  <User className="w-4 h-4 text-[#484C3F] hover:opacity-70 transition-opacity cursor-pointer" />
                </Link>
                <Link href="/mywishlist">
                  <Heart className="w-4 h-4 text-[#484C3F] hover:opacity-70 transition-opacity cursor-pointer" />
                </Link>
                <Link href="/cart" className="relative">
                  <ShoppingBag className="w-4 h-4 text-[#484C3F] hover:opacity-70 transition-opacity cursor-pointer" />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-primary text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Search Bar */}
        {showSearchBar && isMobile && (
          <>
            <div
              className="fixed inset-0 !bg-black/50 z-[999]"
              onClick={toggleSearchBar}
              aria-hidden="true"
            />
            <div className={`fixed top-0 left-0 w-full bg-white shadow-md z-[1000] transition-all duration-300 ${showSearchBar ? "top-0" : "-top-[100px]"}`}>
              <form onSubmit={handleSearchSubmit} className="flex !bg-white items-center gap-2 p-4">
                <button
                  type="button"
                  onClick={toggleSearchBar}
                  className="text-dark-green"
                >
                  <X className="w-6 h-6" />
                </button>
                <input
                  type="text"
                  placeholder="Search for products, brands and more"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  ref={inputRef}
                  className="flex-1 font-arbutus text-base text-dark-green placeholder-placeholder-gray bg-transparent outline-none"
                />
              </form>
            </div>
          </>
        )}

        {/* Sidebar Drawer */}
        {isOpen && (
          <div>
            <div
              className="fixed inset-0 !bg-black/50 backdrop-blur-[0px] z-[80] opacity-100 transition-opacity"
              onClick={toggleMenu}
              aria-hidden="true"
            />
            <aside
              className={`fixed top-0 left-0 h-full w-[80%] !bg-white max-w-sm bg-cream z-[100] shadow-xl transform transition-transform duration-300 ease-out ${isOpen ? "translate-x-0" : "-translate-x-full"}`}
              role="dialog"
              aria-modal="true"
            >
              <div className="h-20 flex items-center justify-between !px-[10px] border-b border-light-green/60">
                {/* <span className="font-arbutus text-dark-green text-lg">Menu</span> */}
                <Link href="/">
                  <Image
                    src="https://assetsvilva.blr1.cdn.digitaloceanspaces.com/LU03502F2509/uploads/AIf4oSmtfOHqW7lGzLH1VmEW2R177u1PBS3dBZaI.webp"
                    alt="Logo"
                    height={100}
                    width={100}
                    className="w-[140px]"
                  />
                </Link>
                <button
                  aria-label="Close menu"
                  onClick={toggleMenu}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-md text-dark-green hover:bg-dark-green/5"
                >
                  <X className="w-4 h-4 text-[#484C3F] hover:opacity-70 transition-opacity cursor-pointer" />
                </button>
              </div>

              <div className="p-2 space-y-6 overflow-y-auto h-[calc(100%-5rem)]">
                <div className="flex items-center gap-2 w-full px-3 py-2 bg-white border border-[#C7DF9C] rounded-[20px]">
                  <Search className="w-4 h-4 text-[#484C3F] hover:opacity-70 transition-opacity cursor-pointer" />
                  <input
                    type="text"
                    placeholder="Search Products"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="flex-1 font-arbutus text-base text-[#484C3F] !placeholder-[#AEAEAE] bg-transparent outline-none"
                  />
                </div>

                <nav>
                  <ul className="flex flex-col">
                    <li>
                      <Link
                        href="/"
                        className="block px-2 py-3 font-arbutus text-dark-green text-lg hover:bg-dark-green/5 rounded-md"
                      >
                        Home
                      </Link>
                    </li>
                    {mainCategories?.map((mainCategory) => (
                      <li key={mainCategory.id}>
                        <div className="flex justify-between items-center px-2 py-3">
                          <Link
                            href={`/products/${mainCategory.seo_url}`}
                            className="font-arbutus text-dark-green text-lg hover:bg-dark-green/5 rounded-md"
                          >
                            {mainCategory.name}
                          </Link>
                          {hasSubcategories(mainCategory.id) && (
                            <ChevronDown
                              className="w-5 h-5 text-dark-green cursor-pointer"
                              onClick={() => toggleCategory(String(mainCategory.id), "expandedMainCategory")}
                            />
                          )}
                        </div>
                        {expandedMainCategory === String(mainCategory.id) && (
                          <ul className="pl-4">
                            {getSubcategoriesByParentId(mainCategory.id).map((subCategory) => (
                              <li key={subCategory.id}>
                                <div className="flex justify-between items-center px-2 py-2">
                                  <Link
                                    href={`/products/${subCategory.seo_url}`}
                                    className="font-arbutus text-dark-green text-base hover:bg-dark-green/5 rounded-md"
                                  >
                                    {subCategory.name}
                                  </Link>
                                  {hasSubcategories(subCategory.id) && (
                                    <ChevronDown
                                      className="w-5 h-5 text-dark-green cursor-pointer"
                                      onClick={() => toggleCategory(String(subCategory.id), "expandedSubCategory")}
                                    />
                                  )}
                                </div>
                                {expandedSubCategory === String(subCategory.id) && (
                                  <ul className="pl-4">
                                    {getSubcategoriesByParentId(subCategory.id).map((subSubCategory) => (
                                      <li key={subSubCategory.id}>
                                        <div className="flex justify-between items-center px-2 py-2">
                                          <Link
                                            href={`/products/${subSubCategory.seo_url}`}
                                            className="font-arbutus text-dark-green text-base hover:bg-dark-green/5 rounded-md"
                                          >
                                            {subSubCategory.name}
                                          </Link>
                                          {hasSubcategories(subSubCategory.id) && (
                                            <ChevronDown
                                              className="w-5 h-5 text-dark-green cursor-pointer"
                                              onClick={() =>
                                                toggleCategory(String(subSubCategory.id), "expandedSubSubCategory")
                                              }
                                            />
                                          )}
                                        </div>
                                        {expandedSubSubCategory === String(subSubCategory.id) && (
                                          <ul className="pl-4">
                                            {getSubcategoriesByParentId(subSubCategory.id).map(
                                              (subSubSubCategory) => (
                                                <li key={subSubSubCategory.id}>
                                                  <Link
                                                    href={`/products/${subSubSubCategory.seo_url}`}
                                                    className="block px-2 py-2 font-arbutus text-dark-green text-base hover:bg-dark-green/5 rounded-md"
                                                  >
                                                    {subSubSubCategory.name}
                                                  </Link>
                                                </li>
                                              )
                                            )}
                                          </ul>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                    {userData && (
                      <>
                        <li>
                          <Link
                            href="/myaccount"
                            className="block px-2 py-3 font-arbutus text-dark-green text-lg hover:bg-dark-green/5 rounded-md"
                          >
                            My Account
                          </Link>
                        </li>
                        <li>
                          <Link
                            href="/myprofile37"
                            className="block px-2 py-3 font-arbutus text-dark-green text-lg hover:bg-dark-green/5 rounded-md"
                          >
                            My Profile
                          </Link>
                        </li>
                        <li>
                          <Link
                            href="/mywallet"
                            className="block px-2 py-3 font-arbutus text-dark-green text-lg hover:bg-dark-green/5 rounded-md"
                          >
                            My Wallet
                          </Link>
                        </li>
                        <li>
                          <Link
                            href="/myorders"
                            className="block px-2 py-3 font-arbutus text-dark-green text-lg hover:bg-dark-green/5 rounded-md"
                          >
                            My Orders
                          </Link>
                        </li>
                      </>
                    )}
                    <li>
                      <Link
                        href="/contactus"
                        className="block px-2 py-3 font-arbutus text-dark-green text-lg hover:bg-dark-green/5 rounded-md"
                      >
                        Contact Us
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/blog"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-2 py-3 font-arbutus text-dark-green text-lg hover:bg-dark-green/5 rounded-md"
                      >
                        Blog
                      </Link>
                    </li>
                    {userData && (
                      <li>
                        <Link
                          href="/signin"
                          className="block px-2 py-3 font-arbutus text-dark-green text-lg hover:bg-dark-green/5 rounded-md"
                        >
                          LogOut
                        </Link>
                      </li>
                    )}
                  </ul>
                </nav>
              </div>
            </aside>
          </div>
        )}
      </header>

    </>
  );

};

export default Navbar;